import { useState, useEffect } from "react"
import { X, FileText, Image, Video, HelpCircle, Download } from "lucide-react"
import TipTapEditor from "./TipTapEditor"
import ImageUpload from "./ImageUpload"
import { useTranslation } from "../i18n"

export default function ContentBlockModal({ 
  isOpen, 
  onClose, 
  blockType, 
  onSave,
  block = null // Bloc existant à éditer
}) {
  const { tObj } = useTranslation()
  const [blockData, setBlockData] = useState({
    title: "",
    content: "",
    imageUrl: "",
    videoUrl: "",
    questions: []
  })

  const [currentQuestion, setCurrentQuestion] = useState({
    question: "",
    type: "multiple", // multiple, single
    points: 1, // Nombre de points pour cette question
    options: ["", "", "", ""],
    correctAnswer: 0,
    correctAnswers: [] // Pour les questions à choix multiple
  })

  const blockIcons = {
    TEXT: FileText,
    IMAGE: Image,
    VIDEO: Video,
    ASSIGNMENT: HelpCircle,
    DOWNLOAD: Download
  }

  const blockColors = {
    TEXT: "blue",
    IMAGE: "green", 
    VIDEO: "purple",
    ASSIGNMENT: "orange",
    DOWNLOAD: "gray"
  }

  const Icon = blockIcons[blockType] || FileText
  const color = blockColors[blockType] || "gray"

  // Pré-remplir les champs si on édite un bloc existant
  useEffect(() => {
    if (block && isOpen) {
     
      
      // Extraire le contenu selon le type de bloc
      let content = ""
      let imageUrl = ""
      let videoUrl = ""
      let questions = []
      
      if (block.content) {
        switch (blockType) {
          case 'TEXT':
            // Essayer différentes structures possibles
            content = block.content.html || 
                     block.content.content || 
                     (typeof block.content === 'string' ? block.content : "") ||
                     ""
            break
          case 'IMAGE':
            imageUrl = block.content.imageUrl || ""
            break
          case 'VIDEO':
            videoUrl = block.content.videoUrl || ""
            break
          case 'ASSIGNMENT':
            content = block.content.instructions || ""
            break
          case 'DOWNLOAD':
            content = block.content.instructions || ""
            break
          default:
            content = block.content || ""
        }
      }
      
      setBlockData({
        title: block.title || "",
        content: content,
        imageUrl: imageUrl,
        videoUrl: videoUrl,
        questions: questions
      })
    } else if (isOpen && !block) {
      // Réinitialiser SEULEMENT pour un nouveau bloc (pas quand on édite)
      setBlockData({
        title: "",
        content: "",
        imageUrl: "",
        videoUrl: "",
        questions: []
      })
    }
  }, [block, isOpen, blockType])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setBlockData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleQuestionChange = (e) => {
    const { name, value } = e.target
    setCurrentQuestion(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleOptionChange = (index, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((option, i) => i === index ? value : option)
    }))
  }

  const handleAddQuestion = () => {
    if (currentQuestion.question.trim()) {
      // Préparer la question avec la structure appropriée selon le type
      const questionToAdd = {
        ...currentQuestion,
      }
      
      setBlockData(prev => ({
        ...prev,
        questions: [...prev.questions, questionToAdd]
      }))
      setCurrentQuestion({
        question: "",
        type: "multiple",
        points: 1,
        options: ["", "", "", ""],
        correctAnswer: 0,
        correctAnswers: []
      })
    }
  }

  const handleRemoveQuestion = (index) => {
    setBlockData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }))
  }

  const handleSave = () => {
    // Créer le contenu selon le type de bloc
    let content = {}
    
    switch (blockType) {
      case 'TEXT':
        content = {
          html: blockData.content,
          wordCount: blockData.content.split(' ').length,
          readingTime: Math.ceil(blockData.content.split(' ').length / 200) // 200 mots par minute
        }
        break
      case 'IMAGE':
        content = {
          imageUrl: blockData.imageUrl,
          alt: blockData.title || "Image",
          caption: blockData.content || ""
        }
        break
      case 'VIDEO':
        content = {
          videoUrl: blockData.videoUrl,
          duration: 0, // À calculer ou demander à l'utilisateur
          transcript: blockData.content || ""
        }
        break
      case 'ASSIGNMENT':
        content = {
          instructions: blockData.content,
          hints: [],
          expectedOutput: ""
        }
        break
      case 'DOWNLOAD':
        content = {
          instructions: blockData.content,
          files: []
        }
        break
      default:
        content = { content: blockData.content }
    }
    
    // Préparer les données à envoyer selon le type d'opération
    const dataToSave = {
      title: blockData.title,
      content: content,
      type: blockType
    }
    
    // Ajouter description si elle existe
    if (blockData.description) {
      dataToSave.description = blockData.description
    }
    
    onSave(dataToSave)
    
    // Réinitialiser les champs SEULEMENT si on crée un nouveau bloc
    if (!block) {
      setBlockData({
        title: "",
        content: "",
        imageUrl: "",
        videoUrl: "",
        questions: []
      })
      setCurrentQuestion({
        question: "",
        type: "multiple",
        points: 1,
        options: ["", "", "", ""],
        correctAnswer: 0,
        correctAnswers: []
      })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-transparent flex items-center justify-center z-50 p-4" style={{backdropFilter: 'blur(8px)'}}>
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`p-2 bg-${color}-100 rounded-lg`}>
              <Icon size={24} className={`text-${color}-600`} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {block ? tObj.modals.contentBlock.editBlock() : tObj.modals.contentBlock.addBlock()} {blockType}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Titre du bloc */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tObj.modals.contentBlock.blockTitle()}
            </label>
            <input
              type="text"
              name="title"
              value={blockData.title}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
              placeholder={tObj.modals.contentBlock.blockTitlePlaceholder()}
            />
          </div>

          {/* Contenu selon le type */}
          {blockType === "TEXT" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {tObj.modals.contentBlock.textContent()}
              </label>
              <TipTapEditor
                content={blockData.content}
                onChange={(html) => setBlockData(prev => ({ ...prev, content: html }))}
                placeholder={tObj.modals.contentBlock.textContentPlaceholder()}
              />
            </div>
          )}

          {blockType === "IMAGE" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {tObj.modals.contentBlock.uploadImage()}
              </label>
              <ImageUpload
                currentImage={blockData.imageUrl}
                onImageChange={(imageUrl) => setBlockData(prev => ({ ...prev, imageUrl }))}
              />
              <p className="text-sm text-gray-500 mt-2">
                {tObj.modals.contentBlock.imageUploadDesc()}
              </p>
            </div>
          )}

          {blockType === "VIDEO" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {tObj.modals.contentBlock.videoUrl()}
              </label>
              <input
                type="url"
                name="videoUrl"
                value={blockData.videoUrl}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                placeholder={tObj.modals.contentBlock.videoUrlPlaceholder()}
              />
              <p className="text-sm text-gray-500 mt-2">
                {tObj.modals.contentBlock.videoUrlDesc()}
              </p>
            </div>
          )}

          {blockType === "ASSIGNMENT" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {tObj.modals.contentBlock.assignmentInstructions()}
              </label>
              <textarea
                name="content"
                value={blockData.content}
                onChange={handleInputChange}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                placeholder={tObj.modals.contentBlock.assignmentPlaceholder()}
              />
              <p className="text-sm text-gray-500 mt-2">
                {tObj.modals.contentBlock.assignmentDesc()}
              </p>
            </div>
          )}

          {blockType === "QUIZ" && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{tObj.modals.contentBlock.quizQuestions()}</h3>
              
              {/* Questions existantes */}
              {blockData.questions.map((question, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{tObj.modals.contentBlock.question()} {index + 1}</h4>
                      <span className="text-xs text-gray-500 mt-1">
                        {question.type === "multiple" && tObj.modals.contentBlock.multipleChoice()}
                        {question.type === "single" && tObj.modals.contentBlock.singleChoice()}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveQuestion(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-gray-700 mb-3">{question.question}</p>
                  <div className="text-xs text-gray-500 mb-2">
                    Points: {question.points || 1} point{question.points > 1 ? 's' : ''}
                  </div>
                  <div className="space-y-2">
                      {question.options && question.options.map((option, optionIndex) => (
                        <div key={optionIndex} className="flex items-center space-x-2">
                          {question.type === "multiple" ? (
                            <input
                              type="checkbox"
                              checked={question.correctAnswers?.includes(optionIndex)}
                              readOnly
                              className="text-green-600"
                            />
                          ) : (
                            <input
                              type="radio"
                              checked={question.correctAnswer === optionIndex}
                              readOnly
                              className="text-green-600"
                            />
                          )}
                          <span className={`text-sm ${
                            (question.correctAnswers?.includes(optionIndex) || question.correctAnswer === optionIndex)
                              ? "text-green-700 font-medium" 
                              : "text-gray-600"
                          }`}>
                            {option}
                          </span>
                        </div>
                      ))}
                    </div>
                </div>
              ))}

              {/* Nouvelle question */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{tObj.modals.contentBlock.addQuestion()}</h4>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tObj.modals.contentBlock.questionType()}
                  </label>
                  <select
                    name="type"
                    value={currentQuestion.type}
                    onChange={(e) => setCurrentQuestion(prev => ({
                      ...prev,
                      type: e.target.value,
                      correctAnswer: 0,
                      correctAnswers: []
                    }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                  >
                    <option value="multiple">{tObj.modals.contentBlock.multipleChoice()}</option>
                    <option value="single">{tObj.modals.contentBlock.singleChoice()}</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tObj.modals.contentBlock.questionText()}
                  </label>
                  <input
                    type="text"
                    name="question"
                    value={currentQuestion.question}
                    onChange={handleQuestionChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                    placeholder={tObj.modals.contentBlock.questionPlaceholder()}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tObj.modals.contentBlock.points()}
                  </label>
                  <input
                    type="number"
                    name="points"
                    value={currentQuestion.points}
                    onChange={(e) => setCurrentQuestion(prev => ({
                      ...prev,
                      points: parseInt(e.target.value) || 1
                    }))}
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                    placeholder="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {tObj.modals.contentBlock.pointsDesc()}
                  </p>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {tObj.modals.contentBlock.answerOptions()}
                    </label>
                    <div className="space-y-2">
                      {currentQuestion.options.map((option, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          {currentQuestion.type === "multiple" ? (
                            <input
                              type="checkbox"
                              checked={currentQuestion.correctAnswers?.includes(index)}
                              onChange={() => {
                                const current = currentQuestion.correctAnswers || []
                                const newAnswers = current.includes(index)
                                  ? current.filter(i => i !== index)
                                  : [...current, index]
                                setCurrentQuestion(prev => ({
                                  ...prev,
                                  correctAnswers: newAnswers
                                }))
                              }}
                              className="text-green-600"
                            />
                          ) : (
                            <input
                              type="radio"
                              name="correctAnswer"
                              checked={currentQuestion.correctAnswer === index}
                              onChange={() => setCurrentQuestion(prev => ({
                                ...prev,
                                correctAnswer: index
                              }))}
                              className="text-green-600"
                            />
                          )}
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                            placeholder={`${tObj.modals.contentBlock.option()} ${index + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {currentQuestion.type === "multiple" 
                        ? tObj.modals.contentBlock.multipleChoiceDesc()
                        : tObj.modals.contentBlock.singleChoiceDesc()}
                    </p>
                  </div>

                <button
                  onClick={handleAddQuestion}
                  disabled={!currentQuestion.question.trim() || currentQuestion.options.every(opt => !opt.trim())}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tObj.modals.contentBlock.addQuestion()}
                </button>
              </div>
            </div>
          )}

          {blockType === "DOWNLOAD" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {tObj.modals.contentBlock.downloadInstructions()}
              </label>
              <textarea
                name="content"
                value={blockData.content}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                placeholder={tObj.modals.contentBlock.downloadPlaceholder()}
              />
              <p className="text-sm text-gray-500 mt-2">
                {tObj.modals.contentBlock.downloadDesc()}
              </p>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {tObj.modals.contentBlock.cancel()}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-[#0f1220] transition-colors"
            >
              {block ? tObj.modals.contentBlock.save() : tObj.modals.contentBlock.add()}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
